import type React from 'react';

import { Avatar, Text } from '@change/ds-core-ui';
import { ComboBox, ComboBoxItem } from '@change/ds-forms';

import { translationKey } from '@change-corgi/core/i18n';
import { useGetStaticAssetUrl } from '@change-corgi/core-react/assets';
import { Translate, useI18n } from '@change-corgi/core-react/i18n';
import { Card, Separator } from '@change-corgi/design-system-legacy/components/content';
import { Box } from '@change-corgi/design-system-legacy/layout';

import type { DecisionMaker as DecisionMakerType } from '#src/app/shared/hooks/decisionMakers';

import { SelectedDecisionMaker } from './components/SelectedDecisionMaker';
import { useDecisionMaker } from './hooks/useDecisionMaker';

export const DecisionMaker: React.FC = () => {
	const getStaticAssetUrl = useGetStaticAssetUrl();
	const { data, actions } = useDecisionMaker();
	const { typedTranslate, translate } = useI18n();

	const getPhotoUrl = (dm: DecisionMakerType): string => {
		if (dm.photo?.userSmall.url) return dm.photo.userSmall.url;
		return dm.isPerson
			? getStaticAssetUrl('/profile-img/default-user-profile.svg')
			: getStaticAssetUrl('/profile-img/default-org-profile.svg');
	};

	return (
		<div id="decision_makers">
			<Text variant="heading" size="x-small" as="p">
				<Translate value="corgi.petition.edit.petition_data.decision_maker.title" />
			</Text>
			<div className="mt-2 mb-6">
				<Text variant="body" className="text-typography-strong">
					<Translate value="corgi.petition.edit.petition_data.decision_maker.description" />{' '}
					<em>
						<Translate value="corgi.petition.edit.petition_data.decision_maker.description_emphasis" />
					</em>
				</Text>
			</div>
			<ComboBox
				name="decision-maker-combobox-field"
				label={typedTranslate(translationKey('corgi.petition.edit.petition_data.decision_maker.title'))}
				labelIsVisible={false}
				placeholder={typedTranslate(translationKey('corgi.petition.edit.petition_data.decision_maker.placeholder'))}
				size="large"
				menuTrigger="input"
				allowsEmptyCollection
				showCaretDownIcon={false}
				inputValue={data.inputValue}
				onInputChange={(value) => actions.handleInputChange(null, value)}
				onSelectionChange={(key) => {
					const dm = data.options.find((o) => String(o.id) === String(key));
					actions.handleChange(null, dm ?? null);
				}}
				items={data.options}
				data-qa="decision-maker-combobox-field"
			>
				{(dm) => (
					<ComboBoxItem
						id={String(dm.id)}
						label={
							dm.isConfirmed
								? dm.displayName
								: translate('corgi.petition.edit.petition_data.decision_maker.create_dm', {
										dm_name: dm.displayName,
									})
						}
						description={dm.isConfirmed ? (dm.summary ?? undefined) : undefined}
						leadingSlot={dm.isConfirmed ? <Avatar alt={dm.displayName} src={getPhotoUrl(dm)} size={32} /> : undefined}
					/>
				)}
			</ComboBox>
			{data.selectedDecisionMakers.length > 0 && (
				<Card aria-live="polite" sx={{ mt: 24 }}>
					<Box
						as="ul"
						role="list"
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'stretch',
							justifyContent: 'space-between',
							textAlign: 'left',
							width: '100%',
						}}
					>
						{data.selectedDecisionMakers.map((decisionMaker, index) => (
							<Box as="li" role="listitem" key={decisionMaker.id}>
								<>
									{index !== 0 && <Separator sx={{ color: 'neutral-grey600' }} />}
									<SelectedDecisionMaker
										decisionMaker={decisionMaker}
										onRemoveDecisionMaker={() => actions.handleOnRemove(decisionMaker)}
										data-qa="decision-maker-combobox-field-option"
									/>
								</>
							</Box>
						))}
					</Box>
				</Card>
			)}
		</div>
	);
};
